<template>
  <div :id="`${elementId}-container`" class="autocomplete">
    <hp6-input-search
      ref="inputSearch"
      :label-text="label ? label : ' '"
      :id="elementId"
      :hide-search-button="hideSearchButton"
      :required="required"
      :help-text="helpText"
      :label-show-field-optional="showOptionalLabel"
      >
      <input
        ref="input"
        v-model="textValue"
        @input="handleInput"
        @keyup="handleKeyUp"
        @keydown="handleKeyDown"
      />
      <ul 
        ref="list" 
        class="autocomplete-result-list" 
        :style="style"
        :aria-label="`${label || 'autocomplete'} suggestions`"
      ></ul>
    </hp6-input-search>
  </div>
</template>

<script setup>

/** 
 *  FCPAutoComplete2 has pretty much exactly the same function as FCPAutoComplete.  The only difference is that it wraps the hp-input-search
 *  component and its function.
 * 
 *  Properties:
 *  elementId:  The elementId to assign to the underlying input field and to be derived to identify the component.
 *  label:  A label for the field
 *  options:  The options to supply to the autocomplete component.  A full list of possibilities can be read about here:  
 *  https://autocomplete.trevoreyre.com/#/javascript-component?id=options.  At the very least you will want to supply an object with the 
 *  following properties: 
 *  {
 *    search: <A function which populates the autocomplete based upon the text typed>
 *    getResultValue: <A function which derives the display value in the list from the object>
 *  }
 * 
 *  modelValue: This prop lets you use the v-model directive on the component.  It binds to the text of the input field.
 *  hideSearchButton:  Whether to hide the searchButton on the control
 *  required:  Whether this field is required.
 */

  import { ref, computed, watch, onMounted } from 'vue'
  import Autocomplete from '@trevoreyre/autocomplete-js'

  const props = defineProps({
    style: Object,
    elementId: String,
    label: String,
    options: Object,
    modelValue: String,
    initialValue: String,
    hideSearchButton: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    helpText: {
      type: String,
      default: '',
    },
    showOptionalLabel: {
      type: Boolean,
      default: true
    },
    //If true, the component will handle the enter key and select the first item in the list.
    handleEnterKey: {
      type: Boolean,
      default: true
    }

  })

    const emit = defineEmits(['update:modelValue', 'onSelected', 'onInputChange'])
    // Refs
    const inputSearch = ref(null)
    const input = ref(null)
    const list = ref(null)

    // State
    const textValue = ref(props.modelValue || props.initialValue || '')
    const activeIndex = ref(-1)
    const modelValue = computed(() => props.modelValue)

    let opts = { ...props.options }

    // Store search results for keyboard navigation
    const currentResults = ref([])

    // Watchers
    watch(textValue, () => {
      emit('update:modelValue', textValue.value)
    })

    watch(modelValue, () => {
      textValue.value = props.modelValue || ''
    })

    // Helper functions for keyboard navigation
    const highlightItem = (index) => {
      const items = list.value?.querySelectorAll('li')
      if (items) {
        items.forEach((item, i) => {
          item.setAttribute('aria-selected', i === index ? 'true' : 'false')
        })
      }
    }

    const clearHighlight = () => {
      const items = list.value?.querySelectorAll('li')
      items?.forEach(item => item.setAttribute('aria-selected', 'false'))
    }

    // Enhanced keyboard navigation
    const handleKeyDown = (event) => {
      if (!currentResults.value.length) return
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          activeIndex.value = activeIndex.value < currentResults.value.length - 1 
            ? activeIndex.value + 1 
            : 0
          highlightItem(activeIndex.value)
          break
          
        case 'ArrowUp':
          event.preventDefault()
          activeIndex.value = activeIndex.value > 0 
            ? activeIndex.value - 1 
            : currentResults.value.length - 1
          highlightItem(activeIndex.value)
          break
          
        case 'Escape':
          activeIndex.value = -1
          currentResults.value = []
          clearHighlight()
          if (list.value) {
            list.value.innerHTML = ''
          }
          break
      }
    }

    const handleKeyUp = (event) => {
      // Only handle Enter for auto-selection
      // Tab should only select if user has actively navigated with arrow keys
      const isEnter = event.key === 'Enter'
      const isTabWithActiveSelection = event.key === 'Tab' && activeIndex.value >= 0
      
      if ((isEnter || isTabWithActiveSelection) && props.handleEnterKey) {
        
        if (currentResults.value.length > 0) {
          // If user navigated with arrows, select that item
          if (activeIndex.value >= 0) {
            const selectedResult = currentResults.value[activeIndex.value]
            const displayValue = opts.getResultValue
              ? opts.getResultValue(selectedResult)
              : (typeof selectedResult === 'string' ? selectedResult : '')
            emit('update:modelValue', displayValue)
            emit('onSelected', selectedResult)
            if (input.value) {
              input.value.value = displayValue
            }
          } else {
            // Original behavior: Select first item if no navigation occurred
            const selectedResult = currentResults.value[0]
            const displayValue = opts.getResultValue
              ? opts.getResultValue(selectedResult)
              : (typeof selectedResult === 'string' ? selectedResult : '')
            emit('update:modelValue', displayValue)
            emit('onSelected', selectedResult)
            if (input.value) {
              input.value.value = displayValue
            }
          }
          
          // Reset state
          activeIndex.value = -1
          currentResults.value = []
          clearHighlight()
        }
        // Don't prevent default - let modal close naturally
      }
    }

    /**
     * Wrap up the user's search method and capture the search results so that we can use it later
     * if/when the user presses the search button in the hp-input-search.
     */
    const doSearch = async (inputText) => {
      try {
        const results = await props.options.search(inputText)
        currentResults.value = results || []
        activeIndex.value = -1 // Reset selection
        return currentResults.value
      } catch (error) {
        console.error('Error in doSearch:', error)
        currentResults.value = []
        return []
      }
    }

    // Enhanced onSubmit to handle both original and keyboard selection
    const onSubmit = (e) => {
      if (e) {
        // e is the raw result object; derive the display string the same way handleKeyUp does
        const displayValue = opts.getResultValue
          ? opts.getResultValue(e)
          : (typeof e === 'string' ? e : '')
        textValue.value = displayValue
        emit('update:modelValue', displayValue)
        emit('onSelected', e)
      }
    }

    // Setup options
    opts.search = doSearch
    opts.onSubmit = onSubmit

    // hp-input-search decorates our own native <input>, so the AutoComplete root can be
    // initialized directly on our container - no need to reach into the component's internals.
    onMounted(() => {
      new Autocomplete(`#${props.elementId}-container`, opts)
    })

    /**
     * When anything changes in the autocomplete text input field, generate an event to let users
     * of the component know that the input field has changed.
     */
    const handleInput = (event) => {
        textValue.value = event.target.value
        emit('onInputChange', textValue.value)
    }

</script>

<style lang="scss">
@use '../../assets/scss/breakpoints' as *;

hp6-input-search {
  width: 100%;
}

.autocomplete-result-list {
  z-index: 10 !important;

  // Text
  font-family: var(--text-body-font-sm);
  font-weight: var(--text-body-weight-sm);
  font-size: var(--text-body-pixels-sm);
  line-height: var(--text-body-line-height-sm);
  letter-spacing: var(--text-body-letter-spacing-sm);
  color: var(--color-text-base);
  overflow-wrap: break-word;
  overflow: hidden;

  // Spacing
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  margin-top: 0px !important;
  margin-bottom: 0px;
  padding-top: 0px;
  border: 1px solid var(--color-neutral-xlight);

  // Layout
  background-color: #fff;
  box-shadow: 0 3px 6px rgba(149, 157, 165, 0.15);
  list-style: none;
  position: absolute;
  width: 100%;

  li[aria-selected='true'],
  li:hover {
    background-color: var(--color-neutral-xlight);
  }

}

 .autocomplete-result-list {
    padding-top: 0px;
    margin-top: 0px;
 }

 /** 
  *  We need to get rid of the z-index on the hp-input-search's .input-wrapper element, otherwise
  *  if autocompletes are stacked together, the dropdown ul ends up going underneath the input field.
  *  PD-3004 is an example.
  */
 .autocomplete {
    hp6-input-search .input-wrapper {
      z-index: unset;
    }
  }
</style>
